import React, { PropsWithChildren, useContext, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, ParamsSerializerOptions } from 'axios';
import axiosRetry, { exponentialDelay, isNetworkError } from 'axios-retry';
import * as Sentry from '@sentry/react';
import qs from 'qs';
import { MessageResponse, ringilApiGenerator, RingilApiType } from './ringil-api.v2';
import { formatBEValidationMessage, TranslateFn } from '@hooks/useBEValidation/formatBEValidationMessage';
import { ApiError, Callbacks, handleCatchError, handleResponse, isSuccess, resolveQueryParams } from './apiRuntime';
import { SUCCESS_TOAST_DENYLIST } from './successToastDenylist';
import { SILENT_API_ERROR_DENYLIST } from './silentApiErrorDenylist';
import { useTranslation } from 'react-i18next';
import { ToastContext } from '../components/auth/ToastContext.tsx';
import { useSearchParams } from 'react-router-dom';
import { createLogger } from '@utils/logger';
import { recordApiFailure, recordApiResponse } from '@components/ringil3/Features/Assistant/pageContext/apiErrors';

const log = createLogger('api');

// Transparently recover from transient failures so they don't reach the user as the generic
// error toast. Users were intermittently seeing "500" popups while clicking around — these
// turned out to be rare edge/origin 5xx and connection cuts (e.g. Cloudflare<->origin) on
// background or orphaned requests, not real application errors (the data always loaded fine).
//
// Only GETs are retried: they're idempotent, whereas the app's POST/PUT/PATCH/DELETE are not
// guaranteed to be, so a network error mid-mutation must surface rather than silently repeat.
// Canceled requests (ERR_CANCELED) are never retried by axios-retry. A persistent error still
// surfaces within ~1s (2 short retries), so genuine outages are not masked.
axiosRetry(axios, {
  retries: 2,
  retryDelay: exponentialDelay,
  retryCondition: (error: AxiosError) =>
    error.config?.method?.toLowerCase() === 'get' && (isNetworkError(error) || (error.response?.status >= 500 && error.response?.status <= 599)),
});

// Mutating HTTP methods. A genuine success on any of these shows the generic green confirmation
// toast, unless the endpoint is denylisted (read-style POST/PUT) or the caller opts out via
// callbacks.successToast === false. GET is excluded — reads never confirm a change.
const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete']);

//@ts-ignore
export const ApiContext = React.createContext<RingilApiType>(undefined);

export const Provider: React.FC<PropsWithChildren> = ({ children }) => {
  const { getIdTokenClaims, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const frontendservicepublicendpoint = '/services/frontend-service';
  const frontendbehost = window.location.origin;
  const { i18n } = useTranslation('common');
  const { t } = useTranslation();
  const { showToastMessage } = useContext(ToastContext);

  const [seachParams] = useSearchParams();
  const runAs = seachParams.get('runAs');

  // Dedupes concurrent token refreshes: a burst of simultaneous 401s triggers a single
  // getAccessTokenSilently call, not one per request.
  const refreshPromiseRef = useRef<Promise<string | undefined> | null>(null);

  const showValidationToast = (messages: MessageResponse[]) => {
    const trAdapter: TranslateFn = (key, defaultText, data, count) => t(key, { defaultValue: defaultText, ...(data ?? {}), ...(count != null ? { count } : {}) }) as string;
    const texts = (messages ?? []).map(m => formatBEValidationMessage(m, trAdapter)).filter(Boolean);
    if (texts.length === 0) return;
    showToastMessage(
      t('api.validationFailedSummary', 'Změnu nelze uložit'),
      <div className="toast-wrapper">
        {texts.map((text, idx) => (
          <div key={idx}>{text}</div>
        ))}
      </div>,
      'error',
    );
  };

  const handleError = (methodName: string, error: ApiError, payload?: any) => {
    log.debug(`error in ${methodName}`, error);
    const anyError = error as any;
    const httpStatus = anyError?.response?.status;
    const trcUUID = anyError?.config?.params?.trcUUID;
    // Non-critical background polls (notification/alert polls) run automatically on a timer — a
    // transient failure has no user value, so don't show the error toast. We still log and report
    // to Sentry so the failure remains visible for monitoring. (Sentry RINGIL-APP-SX.)
    const suppressToast = SILENT_API_ERROR_DENYLIST.has(methodName);
    // Only genuine, user-surfaced failures reach here: transient blips already recovered via the
    // axios-retry pass, and canceled requests are dropped in handleCatchError. Record them in
    // Sentry (which otherwise never sees these caught errors) and emit a structured console line.
    // trcUUID is on the axios request params (resolveQueryParams), tying the event to one call.
    log.error('API error surfaced to user', {
      method: methodName,
      httpStatus,
      httpMethod: error?.config?.method,
      url: error?.config?.url,
      errorCode: anyError?.code,
      trcUUID,
    });
    const isForbidden = httpStatus === 403;
    // A network/timeout error means the request never reached the server (dropped/unstable
    // connection), not a backend failure. (Sentry RINGIL-APP-SY.)
    const isConnectionError = anyError?.code === 'ERR_NETWORK' || anyError?.code === 'ECONNABORTED';
    // Neither of these is an application defect: on a 403 the backend answered correctly and the
    // caller simply lacks the permission, and a transport failure is the user's connection. Report
    // them as warnings so they stay searchable by apiMethod — that is how we find screens fetching
    // data a role cannot access, and how we watch connectivity — without sitting in the error
    // bucket next to real crashes. (Sentry RINGIL-APP-SX / RINGIL-APP-SY.)
    Sentry.captureException(error, {
      level: isForbidden || isConnectionError ? 'warning' : 'error',
      tags: { apiMethod: methodName, httpStatus, httpMethod: error?.config?.method },
      extra: { url: error?.config?.url, trcUUID, errorCode: anyError?.code },
    });
    if (suppressToast) return;
    // Same reasoning on the UI side: tell the user plainly that they are not allowed to do this,
    // and skip the 30s technical dump (message, endpoint, payload) — there is nothing they could
    // act on and nothing to report.
    if (isForbidden) {
      showToastMessage(
        t(`api.errorForbiddenSummary`, `Nemáte oprávnění`),
        t(`api.errorForbiddenDetail`, `K této akci nemáte potřebné oprávnění. Obraťte se na správce společnosti.`),
        'error',
      );
      return;
    }
    // Tell the user their connection dropped, so they know to check it and retry.
    showToastMessage(
      isConnectionError
        ? t(`api.errorConnection`, `Problém s připojením k internetu – zkontrolujte připojení a akci opakujte.`)
        : t(`api.errorApi`, `Nastala chyba při komunikaci s API`),
      <>
        <div className="toast-wrapper">
          <div>{error.message}</div>
          <div>
            <strong>{methodName}</strong> - {error?.config?.method} - {error?.config?.url}
          </div>
          {trcUUID && (
            <div
              className="cursor-pointer underline"
              title={t('api.copyTraceId', 'Click to copy trace ID')}
              onClick={() => {
                navigator.clipboard.writeText(trcUUID).then(() => {
                  showToastMessage(t('api.traceIdCopied', 'Trace ID copied to clipboard'), trcUUID, 'success');
                });
              }}>
              {t('api.traceId', 'Trace ID')}: {trcUUID}
            </div>
          )}
          {payload != null && Object.keys(payload).length > 0 && (
            <>
              <div>Payload</div>
              <div>
                <pre className="long-error-pre">{JSON.stringify(payload, null, 2)}</pre>
              </div>
            </>
          )}
        </div>
      </>,
      'error',
      30000,
    );
  };

  // Shows the green confirmation toast after a successful data-update so the user knows their
  // change was performed. Default: any mutating method (POST/PUT/PATCH/DELETE) that isn't a
  // denylisted read-style endpoint. The caller can override per call via callbacks.successToast
  // (false → suppress, true → force, object → custom wording). Purely additive — runs alongside
  // (never instead of) the caller's onSuccess, and fires only on a genuine success:
  // conflict/validation/additional-action/not-found/error responses never reach here.
  const maybeShowSuccessToast = (method: string, methodName: string, response: AxiosResponse, isBinary: boolean, callbacks: Callbacks) => {
    const opt = callbacks.successToast;
    if (opt === false) return;
    if (opt === undefined && (!MUTATION_METHODS.has(method.toLowerCase()) || SUCCESS_TOAST_DENYLIST.has(methodName))) return;
    const succeeded = isBinary ? response.status >= 200 && response.status < 300 : isSuccess(response);
    if (!succeeded) return;
    const override = typeof opt === 'object' ? opt : undefined;
    showToastMessage(override?.summary ?? t('api.successSummary', 'Hotovo'), override?.detail ?? t('api.successDetail', 'Změna byla uložena'), 'success');
  };

  const makeRequest = (method: string, url: string, data: any, params: any[], headers: Record<string, string> = {}, isBinary: boolean = false) => {
    log.debug(`Sending ${method.toUpperCase()} -> ${url}${isBinary ? ' [binary]' : ''}`, data || {});

    const config: AxiosRequestConfig = {
      headers: {
        'Accept-Language': i18n.language,
        ...headers,
      },
      params: resolveQueryParams(params, runAs),
      paramsSerializer: (params: Record<string, any>, options?: ParamsSerializerOptions) => qs.stringify(params, { arrayFormat: 'repeat' }),
    };

    if (isBinary) {
      config.responseType = 'blob';
    }

    switch (method.toLowerCase()) {
      case 'post':
        return axios.post(url, data, config);
      case 'put':
        return axios.put(url, data, config);
      case 'patch':
        return axios.patch(url, data, config);
      case 'delete':
        // `data` rather than a second positional argument: axios.delete takes only (url, config),
        // so a body passed positionally is read as the config and silently dropped — the request
        // then reaches the backend with no body at all. Undefined `data` sends no body, as before.
        return axios.delete(url, { ...config, data });
      case 'get':
        return axios.get(url, config);
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }
  };

  const getRawIdToken = (): Promise<string | undefined> => getIdTokenClaims().then(claims => claims?.__raw);

  // Forces Auth0 to use the refresh token to mint a new session, then returns the freshly
  // refreshed raw ID token. Concurrent callers share one in-flight refresh.
  const refreshIdToken = (): Promise<string | undefined> => {
    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = getAccessTokenSilently({ cacheMode: 'off' })
        .then(() => getRawIdToken())
        .finally(() => {
          refreshPromiseRef.current = null;
        });
    }
    return refreshPromiseRef.current;
  };

  /**
   * Runs an authenticated call with the current ID token. If the backend answers 401
   * (expired identity token), refreshes the token once and re-executes the call.
   *
   * The refresh is attempted only when Auth0 actually holds a session. Without one there is no
   * refresh token to spend, so getAccessTokenSilently would throw "Missing Refresh Token" and turn
   * a plain "not logged in yet" 401 into a reported error. A 401 without a session means "log in"
   * (the route guard redirects), not "refresh". (Sentry RINGIL-APP-WM.)
   */
  const executeAuthorized = (makeCall: (bearer: string | undefined) => Promise<AxiosResponse>): Promise<AxiosResponse> => {
    return getRawIdToken().then(bearer =>
      makeCall(bearer).catch((error: AxiosError) => {
        if (error.response?.status === 401 && isAuthenticated) {
          log.debug('Identity token might be expired, refreshing and retrying once...');
          return refreshIdToken().then(freshBearer => makeCall(freshBearer));
        }
        throw error;
      }),
    );
  };

  const executeRequest = (
    method: string,
    url: string,
    data: any,
    params: any[],
    isPublic: boolean,
    isBinary: boolean,
    methodName: string,
    onSuccess: (data: any) => void,
    callbacks: Callbacks = {},
    hashAuthentication?: string,
  ): void => {
    // The `= {}` default only covers `undefined`. Callers may pass an explicit `null` for the
    // optional callbacks arg (e.g. AuditLogHighlights), which would slip through and crash later
    // in handleCatchError/handleResponse/maybeShowSuccessToast on `callbacks.<x>`. Normalize once
    // here so every downstream consumer gets a real object. (Sentry RINGIL-APP-ET + onError cluster.)
    callbacks = callbacks ?? {};
    const onResponse = (response: AxiosResponse) => {
      // First, so the assistant's page context sees the envelope's message keys before a page's
      // onValidationFailed localizes them, and before a throwing onSuccess can skip it.
      recordApiResponse(method, url, methodName, response, isBinary);
      // Binary responses are Blobs and must not go through handleResponse (which reads
      // response.data.errorType off the envelope).
      if (isBinary) {
        onSuccess(response.data);
      } else {
        // A VALIDATION envelope with nobody listening used to vanish silently (e.g. inline tags saving
        // outside a form). Fall back to an error toast with the localized backend messages - for mutations
        // only: a read (GET) answering VALIDATION stays silent, the "can't save" toast would make no sense there.
        const withValidationFallback: Callbacks =
          callbacks.onValidationFailed || !MUTATION_METHODS.has(method.toLowerCase())
            ? callbacks
            : { ...callbacks, onValidationFailed: showValidationToast };
        handleResponse(response, onSuccess, withValidationFallback);
      }
      maybeShowSuccessToast(method, methodName, response, isBinary, callbacks);
    };
    const onResponseUnwrappingJsonBlob = (response: AxiosResponse): void | Promise<void> => {
      // A binary endpoint reports errors as HTTP 200 with a JSON ResponseResult, which axios hands
      // over as a Blob; unwrap it so the callbacks fire as for a JSON call.
      if (isBinary && response.data instanceof Blob && response.data.type.startsWith('application/json')) {
        return response.data
          .text()
          .then(text => JSON.parse(text))
          .then(
            parsed => {
              const jsonResponse = { ...response, data: parsed };
              // This path bypasses onResponse: record the unwrapped envelope, as onResponse does for JSON calls.
              recordApiResponse(method, url, methodName, jsonResponse, false);
              handleResponse(jsonResponse, onSuccess, callbacks);
              maybeShowSuccessToast(method, methodName, jsonResponse, false, callbacks);
            },
            () => onResponse(response),
          );
      }
      onResponse(response);
    };
    const onCatch = (error: any) => {
      // Here rather than in handleError: a page passing its own onError never reaches handleError.
      recordApiFailure(method, url, methodName, error);
      handleCatchError(methodName, error, callbacks, handleError, data);
    };

    if (!isPublic && hashAuthentication) {
      makeRequest(method, url, data, params, { Authorization: `Hash ${hashAuthentication}` }, isBinary).then(onResponseUnwrappingJsonBlob).catch(onCatch);
    } else if (!isPublic) {
      executeAuthorized(bearer => makeRequest(method, url, data, params, { Authorization: `Bearer ${bearer}` }, isBinary))
        .then(onResponseUnwrappingJsonBlob)
        .catch(onCatch);
    } else {
      makeRequest(method, url, data, params, {}, isBinary).then(onResponseUnwrappingJsonBlob).catch(onCatch);
    }
  };

  const ringilApiv2 = ringilApiGenerator(frontendbehost + frontendservicepublicendpoint, executeRequest);

  return (
    <ApiContext.Provider value={{ ...ringilApiv2 }}>
      <>{children}</>
    </ApiContext.Provider>
  );
};